// Number formatting utilities
  export const formatNumber = (value, options = {}) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }
  
    const {
      decimals = 0,
      compact = false,
      prefix = '',
      suffix = ''
    } = options;
  
    let formattedValue;
  
    if (compact && Math.abs(value) >= 1000) {
      formattedValue = formatCompactNumber(value, decimals);
    } else {
      formattedValue = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value);
    }
  
    return `${prefix}${formattedValue}${suffix}`;
  };

  // const formatNumber = (value) => {
//   if (value >= 1000000) {
//     return `${(value / 1000000).toFixed(1)}M`;
//   } else if (value >= 1000) {
//     return `${Math.round(value / 1000)}K`;
//   } else {
//     return `${Math.round(value)}`;
//   }
// };

  export const formatLastUpdated = (timestamp, timezone = 'America/New_York') => {
    if (!timestamp) return 'Never';

    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    const actualDateTime = date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
      timeZoneName: 'short'
    });

    let relativeTime;
    if (diffInMinutes < 1) {
      relativeTime = 'Just now';
    } else if (diffInMinutes < 60) {
      relativeTime = `${diffInMinutes}m ago`;
    } else {
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) {
        relativeTime = `${diffInHours}h ago`;
      } else {
        return actualDateTime;
      }
    }

    return `${relativeTime} (${actualDateTime})`;
  };

  // Compact number formatting (1K, 1M, 1B)
  export const formatCompactNumber = (value, decimals = 1) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }
  
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
  
    if (absValue >= 1e9) {
      return `${sign}${(absValue / 1e9).toFixed(decimals)}B`;
    } else if (absValue >= 1e6) {
      return `${sign}${(absValue / 1e6).toFixed(decimals)}M`;
    } else if (absValue >= 1e3) {
      return `${sign}${(absValue / 1e3).toFixed(decimals)}K`;
    }
  
    return `${sign}${absValue.toFixed(decimals)}`;
  };
  
  // Percentage formatting
  export const formatPercentage = (value, decimals = 1) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0%';
    }
  
    return `${(value*100).toFixed(decimals)}%`;
  };
  
  // Date formatting utilities
  export const formatDate = (date, format = 'short') => {
    if (!date) return '';
  
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
  
    const options = {
      short: { month: 'short', day: 'numeric', year: 'numeric' },
      medium: { month: 'long', day: 'numeric', year: 'numeric' },
      long: { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      },
      time: { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      },
      datetime: { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      }
    };
  
    return new Intl.DateTimeFormat('en-US', options[format] || options.short).format(dateObj);
  };
  
  // Format number to 1 decimal place for months
  export const formatMonths = (value) => {
    return value.toFixed(1);
  };

  export const formatTableDate = (dateString) => {
  // Extract just "2025-09-01" (no time/timezone)
  const dateStr = dateString.split('T')[0];
  const [year, month, day] = dateStr.split('-');
  
  // Create date in LOCAL timezone (no UTC conversion)
  const date = new Date(year, month - 1, day);
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

  // Relative time formatting (time ago)
  export const formatTimeAgo = (date) => {
    if (!date) return '';
  
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
  
    const now = new Date();
    const diffInSeconds = Math.floor((now - dateObj) / 1000);
  
    const intervals = [
      { unit: 'year', seconds: 31536000 },
      { unit: 'month', seconds: 2592000 },
      { unit: 'week', seconds: 604800 },
      { unit: 'day', seconds: 86400 },
      { unit: 'hour', seconds: 3600 },
      { unit: 'minute', seconds: 60 }
    ];
  
    for (const interval of intervals) {
      const count = Math.floor(diffInSeconds / interval.seconds);
      if (count >= 1) {
        return `${count} ${interval.unit}${count > 1 ? 's' : ''} ago`;
      }
    }
  
    return 'Just now';
  };
  
  // Duration formatting (e.g., 3h 45m)
  export const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0s';
  
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
  
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`);
  
    return parts.join(' ');
  };
  
  // File size formatting
  export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
  
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };
  
  // Text truncation
  export const truncateText = (text, maxLength = 50, suffix = '...') => {
    if (!text || text.length <= maxLength) {
      return text || '';
    }
  
    return text.substring(0, maxLength - suffix.length) + suffix;
  };
  
  // Phone number formatting
  export const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
  
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
  
    return phoneNumber;
  };
  
  // Email masking
  export const maskEmail = (email) => {
    if (!email || !email.includes('@')) return email;
  
    const [localPart, domain] = email.split('@');
    const maskedLocal = localPart.length > 2 
      ? localPart[0] + '*'.repeat(localPart.length - 2) + localPart.slice(-1)
      : localPart;
  
    return `${maskedLocal}@${domain}`;
  };
  
  // Campaign ID formatting
  export const formatCampaignId = (id) => {
    if (!id) return '';
    return `Campaign ${id}`;
  };
  
  // Metric value formatting based on type
  export const formatMetricValue = (value, type) => {
    switch (type) {
      case 'currency':
        return formatCurrency(value, 'USD', { compact: true });
      case 'percentage':
        return formatPercentage(value);
      case 'number':
        return formatNumber(value, { compact: true });
      default:
        return String(value);
    }
  };
  
  // Status formatting
  export const formatStatus = (status) => {
    const statusMap = {
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Cancelled'
    };
  
    return statusMap[status?.toLowerCase()] || status;
  };
  
  export const formatHours = (hours) => {
    if (!hours || hours === 0) return 0;
    return hours.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  };


    // Currency formatting
  export const formatCurrency = (amount) => {
    if (!amount || amount === 0){
       amount = 0;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };



  // Export all formatters
  const formatters = {
    formatNumber,
    formatCompactNumber,
    formatHours,
    formatCurrency,
    formatPercentage,
    formatDate,
    formatTimeAgo,
    formatDuration,
    formatFileSize,
    truncateText,
    formatPhoneNumber,
    maskEmail,
    formatCampaignId,
    formatMetricValue,
    formatStatus
  };
  
  
  export default formatters;